import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";

import {
  getDefaultAuthMethod,
  getDefaultBucketVendor,
  getDefaultMetastore,
  getDefaultStorageAuthMethod,
  StorageVendor,
} from "@prequel/react";
import { Button, ButtonStyle, SectionExpandable, Spinner } from "@prequel/shared-components";

import EgressIPsInstructions from "../../../components/EgressIPsInstructions";
import TestSourceConnection from "../TestSourceConnection";

import { RequestStatus } from "@/api/client";
import { useOrg } from "@/api/queries/orgs";
import { useCreateSource, useGenerateSourcePublicKey, useSourceForm, useUpdateSource } from "@/api/queries/sources";
import WrapperSelector from "@/components/WrapperSelector";
import ExistingSource from "@/types/sources";
import {
  computeChangedFields,
  PreparedSource,
  prepareSourceFromExisting,
  prepareSourceWithForm,
  Source,
  SourceType,
} from "@/types/sources";
import { composeForm } from "@/types/sources/form";

const ADVANCED_OPTIONS_SECTION_ID = 6;

const defaultSource: Source = {
  type: SourceType.Source,
  name: "",
  vendor: "snowflake",
};

type SourceContext = {
  source?: ExistingSource;
};

const SourceForm = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const validateForm = () => (formRef.current ? formRef.current.reportValidity() : false);

  const context = useOutletContext<SourceContext>();
  const sourceToEdit = context?.source;
  const { data: org } = useOrg();
  const { data: sourceForm } = useSourceForm(org?.id);

  const isEditing = !!sourceToEdit;
  const [source, setSource] = useState<Source>(() =>
    sourceToEdit ? prepareSourceFromExisting(sourceToEdit) : defaultSource,
  );

  const [testStatus, setTestStatus] = useState<RequestStatus>(undefined);
  const createSourceMutation = useCreateSource();
  const updateSourceMutation = useUpdateSource();
  const sourceMutation = isEditing ? updateSourceMutation : createSourceMutation;

  const sourceUsesSshKey = source.use_ssh_tunnel || source.auth_method === "public_key_auth";
  const existingSshKey = sourceToEdit?.existing_ssh_key;
  const { mutate: generateSourcePublicKey, data: newSshKey } = useGenerateSourcePublicKey();

  useEffect(() => {
    if (sourceUsesSshKey && org && !newSshKey) {
      generateSourcePublicKey(org.id);
    }
  }, [sourceUsesSshKey, org, generateSourcePublicKey, newSshKey]);

  // If setSourceField isn’t passed down to memoized children (or isn’t a dependency of another useEffect/useMemo, skip the callback?
  const setSourceField = useCallback(
    (key: keyof Source, value: string | string[] | boolean | number | undefined) => {
      setSource((currentSource) => ({
        ...currentSource,
        [key]: value,
      }));
    },
    [setSource],
  );

  const form = useMemo(() => {
    if (sourceForm) {
      return composeForm(source, sourceForm, isEditing, existingSshKey, newSshKey);
    }
  }, [source, sourceForm, isEditing, existingSshKey, newSshKey]);

  const preparedSource: PreparedSource = useMemo(() => prepareSourceWithForm(source, form), [source, form]);

  const selectedVendor = useMemo(() => {
    if (form) {
      const { fields } = form[0];
      if (fields[0].form_element === "select") {
        return fields[0].enum?.find(({ key }) => key === source.vendor);
      }
    }
  }, [form, source.vendor]);

  // Set the default auth method, bucket vendor, and metastore if we are creating a new source
  useEffect(() => {
    if (isEditing) {
      return;
    }

    const vendorKey = selectedVendor?.key?.toString();
    if (!vendorKey) {
      return;
    }

    setSource((prev) => {
      const next = { ...prev };
      next.auth_method = getDefaultAuthMethod(vendorKey);
      next.bucket_vendor = getDefaultBucketVendor(vendorKey);
      next.bucket_auth_method = getDefaultStorageAuthMethod(next.bucket_vendor as StorageVendor);
      next.metastore = getDefaultMetastore(vendorKey);
      return next;
    });
  }, [isEditing, selectedVendor?.key]);

  // Set the new public key if we are editing and there isn't an existing public key
  useEffect(() => {
    if (selectedVendor && !existingSshKey && newSshKey) {
      const defaultPublicKey = selectedVendor.key === "snowflake" ? newSshKey.pkcs8 : newSshKey.ssh_rsa;

      setSource((currentSource) => {
        // Only update if values have actually changed
        if (currentSource.ssh_public_key === defaultPublicKey && currentSource.public_key === defaultPublicKey) {
          return currentSource;
        }
        return {
          ...currentSource,
          ssh_public_key: defaultPublicKey,
          public_key: defaultPublicKey,
        };
      });
    }
  }, [selectedVendor, existingSshKey, newSshKey]);

  const onSave = () => {
    if (isEditing) {
      const changed = computeChangedFields(sourceToEdit, preparedSource);
      updateSourceMutation.mutate({
        sourceId: sourceToEdit.id,
        source: changed,
        redirectPath: `/sources/${sourceToEdit.id}`,
      });
    } else {
      createSourceMutation.mutate({
        source: preparedSource,
        redirectPath: "/sources",
      });
    }
  };

  // Intercept native form submission, prevent default, and run test
  // We use the default form submission event so that we can borrow the browsers built-in support for handling missing required fields
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave();
  };

  if (!form) {
    return <Spinner />;
  }

  return (
    <div className="pb-16">
      <form className="space-y-8" onSubmit={onSubmit} ref={formRef}>
        {form.map((section) => {
          if (section.id === ADVANCED_OPTIONS_SECTION_ID) {
            return (
              <Fragment key={section.id}>
                <SectionExpandable label="Advanced options">
                  {section.fields.map((field) => (
                    <div key={field.name} className="mb-6">
                      <WrapperSelector
                        field={field}
                        connection={source}
                        setConnectionField={setSourceField}
                        disabled={testStatus === "processing" || sourceMutation.isPending}
                        isEditing={isEditing}
                      />
                    </div>
                  ))}
                </SectionExpandable>
                <div className="h-px w-full bg-gray-200" />
              </Fragment>
            );
          }

          return (
            <Fragment key={section.id}>
              <div key={section.id}>
                {section.id !== 1 && (
                  <>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      {section.title}
                    </label>
                    {section.subtitle ? (
                      <div className="mt-1 mb-4">
                        <div className="mt-1 text-sm text-gray-500">
                          {section.subtitle}
                          {section.id === 3 && selectedVendor && (
                            <>
                              {" For assistance, "}
                              <a
                                href={selectedVendor.docs}
                                target="_blank"
                                rel="noreferrer"
                                className="
                                  font-medium text-primary-600
                                  hover:text-primary-500
                                "
                              >
                                view our documentation on {selectedVendor.display}.{" "}
                              </a>
                              <EgressIPsInstructions />
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <br />
                    )}
                  </>
                )}
                <div className="space-y-4">
                  {section.fields.map((field) => (
                    <div key={field.name}>
                      <WrapperSelector
                        field={field}
                        connection={source}
                        setConnectionField={setSourceField}
                        disabled={testStatus === "processing" || sourceMutation.isPending}
                        isEditing={isEditing}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="h-px w-full bg-gray-200" /> {/* Divider  */}
            </Fragment>
          );
        })}
        <TestSourceConnection
          key={sourceToEdit?.id ?? "new"}
          beforeSubmitTest={validateForm}
          preparedSource={preparedSource}
          existingSource={sourceToEdit}
          onTestStateChange={(state) => setTestStatus(state.status as RequestStatus)}
        />
      </form>
      <div className="mt-8 flex justify-end">
        {isEditing && (
          <Button
            className="mr-3"
            type={ButtonStyle.TERTIARY}
            disabled={testStatus === "processing" || sourceMutation.isPending}
            onClick={() => navigate(-1)}
            text="Cancel"
          />
        )}
        <Button
          type={testStatus !== "success" ? ButtonStyle.TERTIARY : ButtonStyle.PRIMARY}
          disabled={testStatus !== "success" || sourceMutation.isPending}
          onClick={onSave}
          text={
            sourceMutation.isPending ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-white" />
                Saving Source...
              </div>
            ) : (
              "Save Source"
            )
          }
        />
      </div>
    </div>
  );
};

export default SourceForm;
